"use client";

import { c as _c } from "react/compiler-runtime";
import { getShopCartPricingPreview } from "../cart-line-utils";
import type { ShopPromotionMessage } from "../promotion-messaging";
import React, { useEffect, useState } from "react";
import { FiShoppingCart } from "react-icons/fi";
import FloatingActionPill from "@/components/floating-action-pill";
import { Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui";
import { useShopCatalog } from "@/contexts/shop-catalog-provider";
import { formatCurrency } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { ShopFulfillmentMethod } from "@/types/enums/shop-fulfillment-method";
import { ShopCartLine } from "@/types/shop";
import CartSummaryPanel from "./cart-summary-panel";
interface FloatingCartBarProps {
  lines: ShopCartLine[];
  onUpdateQuantity: (lineId: string, quantity: number) => void;
  onRemoveLine: (lineId: string) => void;
  onReplaceLine: (lineId: string, line: ShopCartLine) => void;
  onCheckout: () => void;
  promotionMessages?: ShopPromotionMessage[];
  fulfillmentMethod?: ShopFulfillmentMethod | null;
  className?: string;
}
const itemCount = (lines: ShopCartLine[]): number => lines.reduce((sum, line) => sum + line.quantity, 0);

/**
 * Fixed-positioned cart pill at the bottom of the page. The left side shows
 * "N items · $total" and opens the cart drawer on click; the right side is
 * the Continue CTA that goes to checkout.
 */
const FloatingCartBar: React.FC<FloatingCartBarProps> = t0 => {
  const $ = _c(55);
  const {
    lines,
    onUpdateQuantity,
    onRemoveLine,
    onReplaceLine,
    onCheckout,
    promotionMessages: t1,
    fulfillmentMethod,
    className
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const promotionMessages = t2;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const {
    catalog
  } = useShopCatalog();
  const isEmpty = lines.length === 0;
  const pricingPreview = getShopCartPricingPreview(lines, catalog.items, catalog.shipping, fulfillmentMethod);
  let t3;
  let t4;
  if ($[2] !== isEmpty) {
    t3 = () => {
      if (isEmpty) {
        setDrawerOpen(false);
      }
    };
    t4 = [isEmpty];
    $[2] = isEmpty;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  if (isEmpty) {
    return null;
  }
  let t5;
  if ($[5] !== lines) {
    t5 = itemCount(lines);
    $[5] = lines;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const count = t5;
  let t6;
  if ($[7] !== onCheckout) {
    t6 = () => {
      setDrawerOpen(false);
      onCheckout();
    };
    $[7] = onCheckout;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleContinue = t6;
  const T0 = FloatingActionPill;
  let t7;
  if ($[9] !== className) {
    t7 = cx("motion-preset-slide-up", "fixed bottom-12 left-1/2 z-20 -translate-x-1/2", className);
    $[9] = className;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = "Continue";
  const t9 = true;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setDrawerOpen(true);
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const t11 = "View cart";
  const t12 = "flex items-center";
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <FiShoppingCart aria-hidden="true" className="mr-2 flex-shrink-0 text-foreground-secondary" />;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  const t14 = count === 1 ? "item" : "items";
  let t15;
  if ($[13] !== t14) {
    t15 = <span>{t14}</span>;
    $[13] = t14;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] !== count || $[16] !== t15) {
    t16 = <span>{count} {t15}</span>;
    $[15] = count;
    $[16] = t15;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span className="px-1 text-foreground-secondary">·</span>;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  const t18 = formatCurrency(pricingPreview.totalCents);
  let t19;
  if ($[19] !== t18) {
    t19 = <span>{t18}</span>;
    $[19] = t18;
    $[20] = t19;
  } else {
    t19 = $[20];
  }
  let t20;
  if ($[21] !== t13 || $[22] !== t16 || $[23] !== t17 || $[24] !== t19) {
    t20 = <span className={t12}>{t13}{t16}{t17}{t19}</span>;
    $[21] = t13;
    $[22] = t16;
    $[23] = t17;
    $[24] = t19;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  let t21;
  if ($[26] !== T0 || $[27] !== handleContinue || $[28] !== t10 || $[29] !== t20 || $[30] !== t7) {
    t21 = <T0 className={t7} actionLabel={t8} onAction={handleContinue} actionVisible={t9} onContentClick={t10} contentAriaLabel={t11}>{t20}</T0>;
    $[26] = T0;
    $[27] = handleContinue;
    $[28] = t10;
    $[29] = t20;
    $[30] = t7;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  const t22 = count === 1 ? "item" : "items";
  let t23;
  if ($[32] !== t22) {
    t23 = <span>{t22}</span>;
    $[32] = t22;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <span>)</span>;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== count || $[36] !== t23) {
    t25 = <DrawerHeader containerClassName="pb-4"><DrawerTitle>Your cart ({count} {t23}{t24}</DrawerTitle></DrawerHeader>;
    $[35] = count;
    $[36] = t23;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] !== fulfillmentMethod || $[39] !== handleContinue || $[40] !== lines || $[41] !== onRemoveLine || $[42] !== onReplaceLine || $[43] !== onUpdateQuantity || $[44] !== promotionMessages) {
    t26 = <DrawerBody className="py-2"><CartSummaryPanel lines={lines} onUpdateQuantity={onUpdateQuantity} onRemoveLine={onRemoveLine} onReplaceLine={onReplaceLine} onCheckout={handleContinue} promotionMessages={promotionMessages} fulfillmentMethod={fulfillmentMethod} /></DrawerBody>;
    $[38] = fulfillmentMethod;
    $[39] = handleContinue;
    $[40] = lines;
    $[41] = onRemoveLine;
    $[42] = onReplaceLine;
    $[43] = onUpdateQuantity;
    $[44] = promotionMessages;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== t25 || $[47] !== t26) {
    t27 = <DrawerContent className="bg-background p-6 backdrop-blur-none">{t25}{t26}</DrawerContent>;
    $[46] = t25;
    $[47] = t26;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== drawerOpen || $[50] !== t27) {
    t28 = <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>{t27}</Drawer>;
    $[49] = drawerOpen;
    $[50] = t27;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== t21 || $[53] !== t28) {
    t29 = <>{t21}{t28}</>;
    $[52] = t21;
    $[53] = t28;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  return t29;
};
export default FloatingCartBar;